import { getAllInvoices, getAllStudents } from "../firebase/database";
import { todayStr } from "./helpers";

/**
 * Fetches non-sensitive application data for public context.
 */
export async function getPublicAppCtx() {
  try {
    const students = await getAllStudents();
    const activeStudents = students.filter(s => s.status !== "inactive");
    const staffCount = activeStudents.filter(s => s.role === "staff").length;
    const studentCount = activeStudents.filter(s => s.role !== "staff").length;

    return `
      INSTITUTE CONTEXT:
      - App Name: Pankaj Lifewellness / Pankaj Computers
      - Total Active Members: ${activeStudents.length} (${studentCount} students, ${staffCount} staff)
      - Location: India
      - Focus: Skill Training, Health & Wellness
    `.trim();
  } catch (error) {
    return "Public context unavailable.";
  }
}

/**
 * Fetches current application data to provide context for the AI.
 * @param {string} role - User role (admin, trainer, student, etc.)
 * @param {string} studentId - Unique ID of the student (if role is student)
 */
export async function getLiveAppCtx(role, studentId) {
  try {
    const today = todayStr(); // YYYY-MM-DD
    const [invoices, students] = await Promise.all([
      getAllInvoices(),
      getAllStudents()
    ]);

    const activeStudents = students.filter(s => s.status !== "inactive");
    const isAdmin = role === "admin" || role === "trainer";

    if (isAdmin) {
      // 1. Calculate Today's Invoice Total (ADMIN ONLY)
      const todayInvoices = invoices.filter(inv => inv.date === today);
      const totalTodayInvoices = todayInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
      const countTodayInvoices = todayInvoices.length;

      // 2. Student Stats
      const staffCount = activeStudents.filter(s => s.role === "staff").length;
      const studentCount = activeStudents.filter(s => s.role !== "staff").length;

      // 3. Overall Financials
      const totalAllTimeRevenue = invoices
        .filter(inv => inv.status === "paid")
        .reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

      return `
        LIVE APP CONTEXT (Current Date: ${today}):
        - Role: ${role} (Full Access)
        - Today's Total Sales: ₹${totalTodayInvoices} (${countTodayInvoices} invoices generated today)
        - Total Active Members: ${activeStudents.length} (${studentCount} students, ${staffCount} staff)
        - Total Paid Revenue (Lifetime): ₹${totalAllTimeRevenue}
        - App Name: Pankaj Lifewellness / Pankaj Computers
      `.trim();
    } else if (role === "student" && studentId) {
      // STUDENT PERSONALIZED CONTEXT
      const myProfile = activeStudents.find(s => s.id === studentId) || {};
      const myInvoices = invoices.filter(inv => inv.customerId === studentId);
      const myPending = myInvoices.filter(inv => inv.status !== "paid");
      const totalFeesPaid = myInvoices.filter(inv => inv.status === "paid").reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

      return `
        STUDENT PERSONALIZED CONTEXT:
        - Current Date: ${today}
        - Student Name: ${myProfile.name || "Student"}
        - Roll Number: ${myProfile.roll || "N/A"}
        - Batch: ${myProfile.batch || "N/A"}
        - Your Invoices: Found ${myInvoices.length} total invoices.
        - Your Pending Invoices: ${myPending.length} (Please clear these if any).
        - Total Fees Deposited: ₹${totalFeesPaid}
        - App Name: Pankaj Lifewellness / Pankaj Computers
        (Note: Do not show global institute sales to this user.)
      `.trim();
    }

    // Default restricted context
    return `
      APP CONTEXT:
      - Current Date: ${today}
      - App Name: Pankaj Lifewellness / Pankaj Computers
      - User Role: ${role || "User"}
    `.trim();
  } catch (error) {
    console.error("Error fetching AI context:", error);
    return "Context currently unavailable.";
  }
}
